import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ClipboardPlus, Layers3, Plus, Workflow } from "lucide-react";
import { toast } from "sonner";

import DashboardHeader from "../components/DashboardHeader";
import IntakeBatchDialog from "../components/IntakeBatchDialog";
import IntakeItemDialog from "../components/IntakeItemDialog";
import ProcessingQueueBoard from "../components/ProcessingQueueBoard";

const intakeApi = `${process.env.REACT_APP_BACKEND_URL}/api/intake`;

export default function IntakeWorkspacePage({ user, onLogout }) {
  const [batches, setBatches] = useState([]);
  const [queueItems, setQueueItems] = useState([]);
  const [batchItems, setBatchItems] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [activeView, setActiveView] = useState("intake");
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [updatingQueueId, setUpdatingQueueId] = useState("");

  const selectedBatch = batches.find((batch) => batch.batch_id === selectedBatchId) || null;

  const load = useCallback(async () => {
    const [batchResponse, queueResponse] = await Promise.all([
      axios.get(`${intakeApi}/batches`, { withCredentials: true }),
      axios.get(`${intakeApi}/processing-queue`, { withCredentials: true }),
    ]);
    setBatches(batchResponse.data.batches);
    setQueueItems(queueResponse.data.items);
    setSelectedBatchId((current) => current || batchResponse.data.batches[0]?.batch_id || "");
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Cardfolio couldn’t load Intake."));
  }, [load]);

  useEffect(() => {
    if (!selectedBatchId) {
      setBatchItems([]);
      return;
    }
    axios
      .get(`${intakeApi}/batches/${selectedBatchId}/items`, { withCredentials: true })
      .then((response) => setBatchItems(response.data.items))
      .catch(() => toast.error("Cardfolio couldn’t load the selected batch."));
  }, [selectedBatchId]);

  const createBatch = async (payload) => {
    const response = await axios.post(`${intakeApi}/batches`, payload, { withCredentials: true });
    await load();
    setSelectedBatchId(response.data.batch_id);
    toast.success("Intake batch created.");
  };

  const createItem = async (batchId, payload) => {
    const response = await axios.post(
      `${intakeApi}/batches/${batchId}/items`,
      payload,
      { withCredentials: true },
    );
    await load();
    const batchResponse = await axios.get(`${intakeApi}/batches/${batchId}/items`, {
      withCredentials: true,
    });
    setBatchItems(batchResponse.data.items);
    toast.success(
      response.data.match_status === "matched"
        ? "Card added and matched to frozen v1."
        : "Card added; set needs a manual selection later.",
    );
  };

  const changeQueueStatus = async (queueId, status) => {
    setUpdatingQueueId(queueId);
    try {
      await axios.patch(
        `${intakeApi}/processing-queue/${queueId}`,
        { status },
        { withCredentials: true },
      );
      await load();
      toast.success("Processing status updated.");
    } catch (error) {
      toast.error("Cardfolio couldn’t update this processing status.");
    } finally {
      setUpdatingQueueId("");
    }
  };

  return (
    <div className="dashboard-shell workspace-shell" data-testid="intake-workspace">
      <DashboardHeader user={user} onLogout={onLogout} activePage="Intake" />
      <main className="workspace-main">
        <header className="workspace-page-header">
          <div>
            <p className="eyebrow">Operations</p>
            <h1>Intake & Processing</h1>
            <p>Bring acquired cards into inventory, then move each one toward listing.</p>
          </div>
          <div className="inventory-header-actions">
            <button
              className="admin-secondary-button"
              type="button"
              onClick={() => setBatchDialogOpen(true)}
              data-testid="start-intake-button"
            >
              <Layers3 size={16} /> Start intake
            </button>
            <button
              className="admin-primary-button"
              type="button"
              disabled={!selectedBatch}
              onClick={() => setItemDialogOpen(true)}
              data-testid="add-intake-card-button"
            >
              <Plus size={17} /> Add card
            </button>
          </div>
        </header>

        <div className="workspace-tabs" role="tablist" data-testid="intake-workspace-tabs">
          <button
            className={activeView === "intake" ? "is-active" : ""}
            type="button"
            onClick={() => setActiveView("intake")}
            data-testid="intake-tab"
          >
            <ClipboardPlus size={16} /> Intake batches
          </button>
          <button
            className={activeView === "queue" ? "is-active" : ""}
            type="button"
            onClick={() => setActiveView("queue")}
            data-testid="processing-queue-tab"
          >
            <Workflow size={16} /> Processing queue
          </button>
        </div>

        {activeView === "intake" ? (
          <section className="intake-layout" data-testid="intake-batches-panel">
            <aside className="batch-list-panel">
              <div className="workspace-section-line">
                <p>Active batches</p>
                <span>{batches.length}</span>
              </div>
              <div className="batch-list">
                {batches.map((batch) => (
                  <button
                    className={batch.batch_id === selectedBatchId ? "is-selected" : ""}
                    key={batch.batch_id}
                    type="button"
                    onClick={() => setSelectedBatchId(batch.batch_id)}
                    data-testid={`intake-batch-${batch.batch_id}`}
                  >
                    <strong>{batch.name}</strong>
                    <span>{batch.item_count} cards · {batch.status}</span>
                  </button>
                ))}
              </div>
              {!batches.length && (
                <div
                  className="workspace-empty intake-empty"
                  data-testid="intake-batches-empty-state"
                >
                  <h2>Start with one acquisition.</h2>
                  <p>Create a batch before adding cards to inventory.</p>
                </div>
              )}
            </aside>
            <section className="batch-detail-panel">
              <div className="workspace-section-line">
                <div>
                  <p>{selectedBatch?.name || "Select an intake batch"}</p>
                  <span>
                    {selectedBatch?.purchase_reference || "No purchase reference"}
                  </span>
                </div>
                <span>{batchItems.length} items</span>
              </div>
              <div className="library-table-wrap">
                <table
                  className="library-table"
                  data-testid="intake-items-table"
                >
                  <thead>
                    <tr>
                      <th>Card</th>
                      <th>Set</th>
                      <th>Condition</th>
                      <th>Qty</th>
                      <th>Cost</th>
                      <th>Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchItems.map((item) => (
                      <tr key={item.intake_item_id}>
                        <td>{item.card_name}</td>
                        <td>
                          {item.set_name || "No set"}
                          {item.set_code ? ` · ${item.set_code}` : ""}
                        </td>
                        <td>{item.condition}</td>
                        <td>{item.quantity}</td>
                        <td>${item.unit_cost}</td>
                        <td>
                          <span className={`match-badge ${item.match_status}`}>
                            {item.match_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </section>
        ) : (
          <ProcessingQueueBoard
            items={queueItems}
            onStatusChange={changeQueueStatus}
            updatingId={updatingQueueId}
          />
        )}
      </main>
      <IntakeBatchDialog
        open={batchDialogOpen}
        onOpenChange={setBatchDialogOpen}
        onCreate={createBatch}
      />
      <IntakeItemDialog
        batch={selectedBatch}
        open={itemDialogOpen}
        onOpenChange={setItemDialogOpen}
        onCreate={createItem}
      />
    </div>
  );
}